import { ScheduledTask, Task } from "@/data/models/Task";

export enum TaskSortOrder {
  EARLIEST_START_DATE,
  TASK_TYPE,
  USER,
  APPOINTMENT_DATE,
  PROJECT_NUMBER,
}

const comparisionOrderTasksToSchedule = new Map([
  [
    TaskSortOrder.EARLIEST_START_DATE,
    [TaskSortOrder.EARLIEST_START_DATE, TaskSortOrder.TASK_TYPE],
  ],
  [
    TaskSortOrder.TASK_TYPE,
    [TaskSortOrder.TASK_TYPE, TaskSortOrder.EARLIEST_START_DATE],
  ],
  [TaskSortOrder.PROJECT_NUMBER, [TaskSortOrder.PROJECT_NUMBER]],
]);

const comparisionOrderSchedueledTasks = new Map([
  [
    TaskSortOrder.EARLIEST_START_DATE,
    [TaskSortOrder.EARLIEST_START_DATE, TaskSortOrder.TASK_TYPE],
  ],
  [
    TaskSortOrder.TASK_TYPE,
    [
      TaskSortOrder.TASK_TYPE,
      TaskSortOrder.APPOINTMENT_DATE,
      TaskSortOrder.USER,
    ],
  ],
  [TaskSortOrder.USER, [TaskSortOrder.USER, TaskSortOrder.TASK_TYPE]],
  [
    TaskSortOrder.APPOINTMENT_DATE,
    [TaskSortOrder.APPOINTMENT_DATE, TaskSortOrder.USER],
  ],
  [TaskSortOrder.PROJECT_NUMBER, [TaskSortOrder.PROJECT_NUMBER]],
]);

export function localizeTaskSortOrder(vue: Vue, taskSortOrder: TaskSortOrder) {
  const prefix = "scheduling.tasks.sortOrder";

  switch (taskSortOrder) {
    case TaskSortOrder.EARLIEST_START_DATE:
      return vue.$t(`${prefix}.earliestStartDate`);
    case TaskSortOrder.TASK_TYPE:
      return vue.$t(`${prefix}.taskType`);
    case TaskSortOrder.USER:
      return vue.$t(`${prefix}.user`);
    case TaskSortOrder.APPOINTMENT_DATE:
      return vue.$t(`${prefix}.appointmentDate`);
    case TaskSortOrder.PROJECT_NUMBER:
      return vue.$t(`${prefix}.projectNumber`);
  }
}

function compareTasksByEarliestStartDate(a: Task, b: Task) {
  return a.earliestStartDate.getTime() - b.earliestStartDate.getTime();
}

function compareTasksByTaskType(a: Task, b: Task) {
  return a.taskType - b.taskType;
}

function compareTaskByProjectNumber(a: Task, b: Task) {
  const aValue = a.projectNumber == "" ? a.projectName : a.projectNumber;
  const bValue = b.projectNumber == "" ? b.projectName : b.projectNumber;

  return aValue.localeCompare(bValue);
}

function compareTaskByUser(
  a: ScheduledTask,
  b: ScheduledTask,
  userSortOrderLookup: Map<string, number>
) {
  return (
    (userSortOrderLookup.get(a.userId) ?? 0) -
    (userSortOrderLookup.get(b.userId) ?? 0)
  );
}

function compareTaskByAppointmentDate(a: ScheduledTask, b: ScheduledTask) {
  return a.startDate.getTime() - b.startDate.getTime();
}

function compareByTaskType(
  a: Task,
  b: Task,
  taskSortOrderByPriority: TaskSortOrder[],
  userSortOrderLookup: Map<string, number>
): number {
  let cmp = 0;

  for (const taskSortOrder of taskSortOrderByPriority) {
    switch (taskSortOrder) {
      case TaskSortOrder.EARLIEST_START_DATE:
        cmp = compareTasksByEarliestStartDate(a, b);
        break;
      case TaskSortOrder.PROJECT_NUMBER:
        cmp = compareTaskByProjectNumber(a, b);
        break;
      case TaskSortOrder.TASK_TYPE:
        cmp = compareTasksByTaskType(a, b);
        break;
      case TaskSortOrder.USER:
        cmp = compareTaskByUser(
          a as ScheduledTask,
          b as ScheduledTask,
          userSortOrderLookup
        );
        break;
      case TaskSortOrder.APPOINTMENT_DATE:
        cmp = compareTaskByAppointmentDate(
          a as ScheduledTask,
          b as ScheduledTask
        );
        break;
    }

    if (cmp != 0) {
      return cmp;
    }
  }

  return cmp;
}

export function sortTasks(
  sortOrder: TaskSortOrder,
  tasks: Task[],
  userSortOrderLookup: Map<string, number>
) {
  const taskSortOrderByPriority =
    comparisionOrderTasksToSchedule.get(sortOrder) ?? [];

  return tasks.sort((a, b) =>
    compareByTaskType(a, b, taskSortOrderByPriority, userSortOrderLookup)
  );
}

export function sortScheduledTasks(
  sortOrder: TaskSortOrder,
  tasks: ScheduledTask[],
  userSortOrderLookup: Map<string, number>
) {
  const taskSortOrderByPriority =
    comparisionOrderSchedueledTasks.get(sortOrder) ?? [];

  return tasks.sort((a, b) =>
    compareByTaskType(a, b, taskSortOrderByPriority, userSortOrderLookup)
  );
}
